import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Tooltip, Collapse, IconButton } from "@mui/material";
import { observer } from "mobx-react";
import { useStores } from "../../use-stores";
import { EmployeeRoles } from "../../models/Employees/EmployeeRoles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartFlatbed,
  faBox,
  faWarehouse,
  faTruckRampBox,
  faUserShield,
  faUserAstronaut,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BoltIcon from "@mui/icons-material/Bolt";

interface QuickLink {
  label: string;
  path: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  description: string;
  /** User must have at least one of these roles (same semantics as `userStore.hasPermission`). */
  roles: EmployeeRoles[];
}

const QUICK_LINKS: QuickLink[] = [
  {
    label: "Add Order",
    path: "/orders/add-orders",
    icon: <FontAwesomeIcon icon={faCartFlatbed} />,
    color: "#059669",
    bgColor: "#ECFDF5",
    description: "Add new order",
    roles: [EmployeeRoles.CREATE_ORDER, EmployeeRoles.EDIT_ORDER],
  },
  {
    label: "Orders",
    path: "/orders/listing",
    icon: <FontAwesomeIcon icon={faCartFlatbed} />,
    color: "#4338CA",
    bgColor: "#EEF2FF",
    description: "Browse all orders",
    roles: [
      EmployeeRoles.VIEW_ORDER,
      EmployeeRoles.CREATE_ORDER,
      EmployeeRoles.EDIT_ORDER,
    ],
  },
  {
    label: "Projects",
    path: "/project-management/listing",
    icon: <FontAwesomeIcon icon={faBox} />,
    color: "#DC2626",
    bgColor: "#FEF2F2",
    description: "Projects",
    roles: [
      EmployeeRoles.VIEW_ORDER,
      EmployeeRoles.CREATE_ORDER,
      EmployeeRoles.EDIT_ORDER,
      EmployeeRoles.EDIT_PROJECT,
    ],
  },
  {
    label: "Products",
    path: "/products/listing",
    icon: <FontAwesomeIcon icon={faBox} />,
    color: "#0369A1",
    bgColor: "#E0F2FE",
    description: "Product catalog",
    roles: [
      EmployeeRoles.PRODUCT_DISPLAY,
      EmployeeRoles.CREATE_PRODUCT,
      EmployeeRoles.EDIT_PRODUCT,
    ],
  },
  {
    label: "Inventory",
    path: "/inventory/listing",
    icon: <FontAwesomeIcon icon={faWarehouse} />,
    color: "#6D28D9",
    bgColor: "#EDE9FE",
    description: "Stock levels",
    roles: [EmployeeRoles.VIEW_INVENTORY, EmployeeRoles.CREATE_INVENTORY],
  },
  {
    label: "Shipments",
    path: "/inventory/shipments",
    icon: <FontAwesomeIcon icon={faTruckRampBox} />,
    color: "#0891B2",
    bgColor: "#ECFEFF",
    description: "Track shipments",
    roles: [
      EmployeeRoles.VIEW_SHIPMENTS,
      EmployeeRoles.CREATE_SHIPMENTS,
      EmployeeRoles.EDIT_SHIPMENTS,
    ],
  },
  {
    label: "Customers",
    path: "/customers/listing",
    icon: <FontAwesomeIcon icon={faUserShield} />,
    color: "#059669",
    bgColor: "#ECFDF5",
    description: "Customer accounts",
    roles: [
      EmployeeRoles.VIEW_CUSTOMER,
      EmployeeRoles.CREATE_CUSTOMER,
      EmployeeRoles.EDIT_CUSTOMER,
    ],
  },
  {
    label: "Vendors",
    path: "/vendors/listing",
    icon: <FontAwesomeIcon icon={faUserAstronaut} />,
    color: "#D97706",
    bgColor: "#FFFBEB",
    description: "Vendor management",
    roles: [EmployeeRoles.VIEW_VENDORS, EmployeeRoles.EDIT_VENDORS],
  },
  {
    label: "Order Invoices",
    path: "/invoices/order-invoices",
    icon: <FontAwesomeIcon icon={faFileInvoice} />,
    color: "#DC2626",
    bgColor: "#FEF2F2",
    description: "Order invoices",
    roles: [
      EmployeeRoles.CREATE_INVOICE_AND_PI,
      EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
      EmployeeRoles.EDIT_INVOICE,
    ],
  },
  {
    label: "Vendor Invoices",
    path: "/invoices/vendor-invoices",
    icon: <FontAwesomeIcon icon={faFileInvoice} />,
    color: "#0369A1",
    bgColor: "#E0F2FE",
    description: "Vendor invoices",
    roles: [
      EmployeeRoles.CREATE_INVOICE_AND_PI,
      EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
      EmployeeRoles.EDIT_INVOICE,
    ],
  },
];

const QuickLinks: React.FC = () => {
  const { userStore } = useStores();
  const [expanded, setExpanded] = useState(true);

  const visibleLinks = QUICK_LINKS.filter((link) =>
    userStore.hasPermission(link.roles),
  );

  if (visibleLinks.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: expanded ? "1px solid #F1F5F9" : "none",
          bgcolor: "#FAFAFE",
          cursor: "pointer",
          "&:hover": { bgcolor: "#F5F3FF" },
          transition: "background 0.15s",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BoltIcon sx={{ fontSize: 16, color: "#4338CA" }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#4338CA",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Quick Access
          </Typography>
        </Box>
        <IconButton size="small" sx={{ p: 0.25, color: "#94A3B8" }}>
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>

      {/* Links grid */}
      <Collapse in={expanded} timeout="auto">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 0.75, sm: 1 },
            p: { xs: 1, sm: 1.5 },
          }}
        >
          {visibleLinks.map((link) => (
            <Tooltip
              key={link.label}
              title={link.description}
              placement="top"
              arrow
            >
              <Link to={link.path} style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.6, sm: 1 },
                    px: { xs: 0.9, sm: 1.25 },
                    py: { xs: 0.6, sm: 0.75 },
                    borderRadius: "8px",
                    border: `1px solid transparent`,
                    bgcolor: link.bgColor,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${link.color}28`,
                      borderColor: `${link.color}40`,
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: { xs: 11, sm: 13 },
                      color: link.color,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {link.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 11, sm: 12 },
                      fontWeight: 600,
                      color: link.color,
                      fontFamily: "Inter, sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {link.label}
                  </Typography>
                </Box>
              </Link>
            </Tooltip>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default observer(QuickLinks);
